import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { NodeToolbar, Position, useStore } from "@xyflow/react"
import { Volume2, VolumeX } from "lucide-react"
import { PlayBadge } from "@/components/media/asset-media-overlay"
import { TimelineLanes } from "@/components/timeline-player/timeline-lanes"
import {
  PlaybackTransport,
  PlayerIconButton,
  usePlaybackTransport,
} from "@/components/timeline-player/playback-transport"
import { useTranslation } from "@/hooks/use-translation"
import { useFlowProjectId } from "@/components/flow/flow-project-context"
import { fetchFlowNodePlayback, type FlowShotPlayback } from "@/lib/flow-api"
import { flowNodeAspectRatio, flowNodeWidth } from "@/lib/flow-node-size"
import { flowTimelinePlaqueWidth } from "@/lib/flow-timeline-width"
import {
  buildSingleVideoDocument,
  dropStaleAnimaticTracks,
  timelineDuration,
  useTimelinePlayback,
  type EngineSources,
  type PlaybackTimeStore,
  type TimelineDocument,
  type TimelineSource,
} from "@/lib/timeline"
import { DEFAULT_VIDEO_OUTPUT_FPS } from "@/lib/video-output-fps"
import { cn } from "@/lib/utils"
import { isFlowBoardTypingTarget } from "@/hooks/use-flow-board-history"
import type { FlowNodeRow } from "@/types/flow"

/** Opaque id клипа канвас-генерации. Совпадает с It2tv::Canvas::NormalizeTimeline::SOURCE_ID. */
const FLOW_CLIP_SOURCE_ID = 1
const EMPTY_SELECTION = new Map<string, readonly string[]>()
const playbackCache = new Map<string, FlowShotPlayback | null>()
/** Кто сейчас звучит. Новый claim ставит предыдущие плееры на паузу. */
const soundClaims = new Set<() => void>()

type WebkitFullscreenVideo = HTMLVideoElement & {
  webkitExitFullscreen?: () => void
}

/** Системный плеер видео внутри transform холста рисует белый экран. Закрываем его. */
function exitAccidentalFlowVideoFullscreen(el: HTMLVideoElement) {
  const leave = () => {
    const video: WebkitFullscreenVideo = el
    if (typeof video.webkitExitFullscreen === "function") {
      try {
        video.webkitExitFullscreen()
      } catch {
        // Не в системном плеере — InvalidStateError.
      }
    }
    if (document.fullscreenElement === el) void document.exitFullscreen().catch(() => {})
  }
  leave()
  requestAnimationFrame(leave)
}

function claimFlowSound(release: () => void) {
  for (const other of [...soundClaims]) {
    if (other !== release) other()
  }
  soundClaims.add(release)
}

type FlowNodePlayerProps = {
  data: FlowNodeRow
  selected: boolean
  /** Двойной клик по кадру. Одинарный запуск ждёт и не срабатывает вместе с ним. */
  onOpen?: () => void
}

const PLAY_CLICK_DELAY_MS = 250

function formatFlowClock(seconds: number): string {
  const total = Number.isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0
  const minutes = Math.floor(total / 60)
  const secs = total % 60
  return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`
}

function storedTimeline(node: FlowNodeRow): TimelineDocument | null {
  const stored = node.editor_timeline
  if (stored && Array.isArray(stored.tracks) && stored.tracks.length > 0) return stored
  return null
}

function shotDocument(playback: FlowShotPlayback): TimelineDocument | null {
  const raw = playback.timeline
  if (!raw || !Array.isArray(raw.tracks) || raw.tracks.length === 0) return null
  return dropStaleAnimaticTracks(raw, playback.animatic?.segment_id ?? null)
}

function sourcesFromShot(playback: FlowShotPlayback): EngineSources {
  const versionUrls: Record<number, string> = {}
  const scrubUrls: Record<number, string> = {}
  const scrubDurationsMs: Record<string, number> = {}
  for (const source of Object.values(playback.sources)) {
    if (source.stream_url) versionUrls[source.version_id] = source.stream_url
    if (source.scrub_url && source.duration_ms) {
      scrubUrls[source.version_id] = source.scrub_url
      scrubDurationsMs[source.scrub_url] = source.duration_ms
    }
  }
  const assetUrls: Record<number, string> = {}
  for (const asset of playback.audio_assets) {
    if (!asset.url) continue
    assetUrls[asset.id] = asset.url
    if (asset.duration_ms && asset.scrub_ok) scrubDurationsMs[asset.url] = asset.duration_ms
  }
  const animatic = playback.animatic
  if (animatic?.scrub_url && animatic.duration_ms) scrubDurationsMs[animatic.scrub_url] = animatic.duration_ms
  return {
    versionUrls,
    assetUrls,
    scrubUrls,
    scrubDurationsMs,
    animaticUrl: animatic?.stream_url ?? null,
    animaticScrubUrl: animatic?.scrub_url ?? null,
  }
}

function animaticSource(playback: FlowShotPlayback | null): TimelineSource | null {
  const animatic = playback?.animatic
  if (!animatic?.playable || !animatic.stream_url) return null
  return {
    version_id: animatic.segment_id,
    stream_url: animatic.stream_url,
    scrub_url: animatic.scrub_url,
    duration_ms: animatic.duration_ms,
    frame_rate: animatic.frame_rate,
    aspect_ratio: animatic.aspect_ratio,
    timeline_frame_urls: animatic.timeline_frame_urls,
  }
}

function FlowPlayClock({ store, durationMs }: { store: PlaybackTimeStore; durationMs: number }) {
  const currentRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    let last = ""
    const update = () => {
      const text = `${formatFlowClock(store.get() / 1000)} - ${formatFlowClock(durationMs / 1000)}`
      if (text === last) return
      last = text
      if (currentRef.current) currentRef.current.textContent = text
    }
    update()
    return store.subscribe(update)
  }, [store, durationMs])

  return (
    <span className="pointer-events-none absolute top-1.5 right-1.5 z-30 rounded-sm bg-black/60 px-1.5 py-0.5 text-[11px] text-white tabular-nums">
      <span ref={currentRef} />
    </span>
  )
}

export function FlowNodePlayer({ data, selected, onOpen }: FlowNodePlayerProps) {
  const { t } = useTranslation("flow")
  const projectId = useFlowProjectId()
  const [hovered, setHovered] = useState(false)
  const [muted, setMuted] = useState(false)
  const [stageKept, setStageKept] = useState(false)
  const [viewportOn, setViewportOn] = useState(false)
  const [stageEpoch, setStageEpoch] = useState(0)
  const [probedMs, setProbedMs] = useState<number | null>(null)
  const [frameReady, setFrameReady] = useState(false)
  const [loaded, setLoaded] = useState<{ key: string; playback: FlowShotPlayback | null } | null>(null)
  const mediaUrl = data.media_url
  const posterUrl = data.media_thumb_url || data.timeline_frame_urls?.[0] || null
  const engaged = hovered || selected
  const measuredWidth = useStore((state) => state.nodeLookup.get(String(data.id))?.measured?.width ?? 0)
  const playbackKey = data.kind === "shot" && data.shot_id != null && mediaUrl ? `${data.shot_id}:${mediaUrl}` : null
  const playbackSettled = playbackKey == null || loaded?.key === playbackKey
  const shotAwaiting = playbackKey != null && !playbackSettled
  const shotPlayback = playbackKey != null && loaded?.key === playbackKey ? loaded.playback : null
  const showStage = (stageKept || engaged) && !shotAwaiting

  const mediaUrlRef = useRef(mediaUrl)
  const stageElRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    setProbedMs(null)
    setFrameReady(false)
  }, [mediaUrl])

  useEffect(() => {
    if (engaged) setStageKept(true)
  }, [engaged])

  useEffect(() => {
    if (!playbackKey || projectId == null || data.shot_id == null) return
    if (!hovered && !selected) return
    if (playbackCache.has(playbackKey)) {
      setLoaded({ key: playbackKey, playback: playbackCache.get(playbackKey) ?? null })
      return
    }
    let cancelled = false
    void fetchFlowNodePlayback(projectId, data.id)
      .then((playback) => {
        playbackCache.set(playbackKey, playback)
        if (!cancelled) setLoaded({ key: playbackKey, playback })
      })
      .catch(() => {
        if (!cancelled) setLoaded({ key: playbackKey, playback: null })
      })
    return () => {
      cancelled = true
    }
  }, [data.id, data.shot_id, hovered, playbackKey, projectId, selected])

  const stored = storedTimeline(data)
  const shotDoc = useMemo(() => (shotPlayback ? shotDocument(shotPlayback) : null), [shotPlayback])
  const singleDoc = useMemo(
    () => buildSingleVideoDocument({ sourceId: FLOW_CLIP_SOURCE_ID, durationMs: data.duration_ms ?? probedMs }),
    [data.duration_ms, probedMs],
  )
  const doc = shotDoc ?? stored ?? singleDoc
  const usingShotDoc = shotDoc != null

  const clipSources = useMemo<EngineSources>(() => {
    const versionUrls: Record<number, string> = {}
    if (mediaUrl) versionUrls[FLOW_CLIP_SOURCE_ID] = mediaUrl
    return { versionUrls, assetUrls: {} }
  }, [mediaUrl])
  const shotSources = useMemo(() => (shotPlayback ? sourcesFromShot(shotPlayback) : null), [shotPlayback])
  const sources = usingShotDoc && shotSources ? shotSources : clipSources

  const aspect = flowNodeAspectRatio(data)
  const clipTimelineSources = useMemo<Record<string, TimelineSource>>(
    () => ({
      [String(FLOW_CLIP_SOURCE_ID)]: {
        version_id: FLOW_CLIP_SOURCE_ID,
        stream_url: mediaUrl,
        duration_ms: data.duration_ms ?? probedMs,
        frame_rate: DEFAULT_VIDEO_OUTPUT_FPS,
        aspect_ratio: aspect,
        timeline_frame_urls: data.timeline_frame_urls ?? [],
      },
    }),
    [aspect, data.duration_ms, data.timeline_frame_urls, mediaUrl, probedMs],
  )
  const timelineSources = usingShotDoc && shotPlayback ? shotPlayback.sources : clipTimelineSources
  const animatic = useMemo(() => (usingShotDoc ? animaticSource(shotPlayback) : null), [shotPlayback, usingShotDoc])

  const onAutoplayBlocked = useCallback(() => setMuted(true), [])

  const onVersionDuration = useCallback(
    (_versionId: number, durationMs: number) => {
      if (stored || usingShotDoc) return
      if (data.duration_ms != null && data.duration_ms > 0) return
      setProbedMs((prev) => (prev === durationMs ? prev : durationMs))
    },
    [data.duration_ms, stored, usingShotDoc],
  )

  const playback = useTimelinePlayback({ doc, sources, onVersionDuration, onAutoplayBlocked })
  const { controls, timeStore, playing, viewportRef } = playback
  const playingRef = useRef(playing)
  playingRef.current = playing
  const selectedAtPointerDown = useRef(false)
  const pendingToggleRef = useRef(false)
  const [playbackNonce, setPlaybackNonce] = useState(0)
  const requestToggle = useCallback(() => {
    pendingToggleRef.current = true
    setPlaybackNonce((nonce) => nonce + 1)
  }, [])
  const playClickTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cancelPlayClick = useCallback(() => {
    if (playClickTimer.current == null) return
    clearTimeout(playClickTimer.current)
    playClickTimer.current = null
  }, [])

  useEffect(() => cancelPlayClick, [cancelPlayClick])
  const stageRef = useCallback(
    (node: HTMLDivElement | null) => {
      stageElRef.current = node
      viewportRef(node)
      setViewportOn(node != null)
      if (node) setStageEpoch((epoch) => epoch + 1)
    },
    [viewportRef],
  )

  useEffect(() => {
    const root = stageElRef.current
    if (!root || !viewportOn) return
    const painted = (el: EventTarget | null) =>
      el instanceof HTMLVideoElement && el.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && el.videoWidth > 0
    const onFrame = (event: Event) => {
      if (painted(event.target)) setFrameReady(true)
    }
    root.addEventListener("loadeddata", onFrame, true)
    root.addEventListener("seeked", onFrame, true)
    setFrameReady(painted(root.querySelector("video")))
    return () => {
      root.removeEventListener("loadeddata", onFrame, true)
      root.removeEventListener("seeked", onFrame, true)
    }
  }, [mediaUrl, stageEpoch, viewportOn])

  useEffect(() => {
    const root = stageElRef.current
    if (!root || !viewportOn) return
    const harden = (el: EventTarget | null) => {
      if (el instanceof HTMLVideoElement && root.contains(el)) el.disablePictureInPicture = true
    }
    for (const video of root.querySelectorAll("video")) harden(video)
    const onMedia = (event: Event) => harden(event.target)
    const onBegin = (event: Event) => {
      const el = event.target
      if (!(el instanceof HTMLVideoElement) || !root.contains(el)) return
      event.preventDefault()
      exitAccidentalFlowVideoFullscreen(el)
    }
    const onFullscreen = () => {
      const current = document.fullscreenElement
      if (current instanceof HTMLVideoElement && root.contains(current)) exitAccidentalFlowVideoFullscreen(current)
    }
    root.addEventListener("webkitbeginfullscreen", onBegin, true)
    root.addEventListener("loadeddata", onMedia, true)
    document.addEventListener("fullscreenchange", onFullscreen)
    return () => {
      root.removeEventListener("webkitbeginfullscreen", onBegin, true)
      root.removeEventListener("loadeddata", onMedia, true)
      document.removeEventListener("fullscreenchange", onFullscreen)
    }
  }, [stageEpoch, viewportOn])

  useEffect(() => {
    controls.setLoop(true)
    controls.setMasterMuted(muted)
  }, [controls, muted])

  useEffect(() => {
    const release = () => {
      soundClaims.delete(release)
      controls.pause()
    }
    if (!playing || muted) return
    claimFlowSound(release)
    return () => {
      soundClaims.delete(release)
    }
  }, [controls, muted, playing])

  useEffect(() => {
    if (!viewportOn || shotAwaiting) return
    if (mediaUrlRef.current !== mediaUrl) {
      mediaUrlRef.current = mediaUrl
      controls.seek(0)
    }
    if (pendingToggleRef.current) {
      pendingToggleRef.current = false
      controls.toggle()
      return
    }
    // Наведение не запускает ролик. Уход с невыбранной ноды останавливает его.
    if (!hovered && !selected) controls.pause()
  }, [controls, hovered, mediaUrl, playbackNonce, selected, shotAwaiting, stageEpoch, viewportOn])

  useEffect(() => {
    if (!selected) return
    const onKey = (event: KeyboardEvent) => {
      if (event.code !== "Space" || event.repeat) return
      if (event.metaKey || event.ctrlKey || event.altKey) return
      if (isFlowBoardTypingTarget(event.target)) return
      if (document.querySelector("[role='dialog'], [role='alertdialog']")) return
      event.preventDefault()
      event.stopImmediatePropagation()
      controls.toggle()
    }
    window.addEventListener("keydown", onKey, true)
    return () => window.removeEventListener("keydown", onKey, true)
  }, [controls, selected])

  const composedMs = timelineDuration(doc)
  const rulerDurationMs = usingShotDoc
    ? Math.max(composedMs, 1)
    : Math.max(data.duration_ms ?? 0, probedMs ?? 0, composedMs, 1)
  const clockDurationMs =
    usingShotDoc || stored
      ? Math.max(composedMs, data.duration_ms ?? 0)
      : Math.max(data.duration_ms ?? 0, probedMs ?? 0)
  const nodeWidth = measuredWidth > 0 ? measuredWidth : flowNodeWidth(aspect, data.display_width)
  const plaqueWidth = flowTimelinePlaqueWidth(nodeWidth, rulerDurationMs)
  const { togglePlay, stepFrames, goToStart, goToEnd } = usePlaybackTransport({
    controls,
    playing,
    frameMs: 1000 / DEFAULT_VIDEO_OUTPUT_FPS,
    getEndMs: () => rulerDurationMs,
  })

  const pauseForScrub = useCallback(() => controls.pause(), [controls])
  const seek = useCallback((ms: number) => controls.scrubTo(ms), [controls])
  const endScrub = useCallback(() => controls.endScrub(), [controls])

  return (
    <div
      className="absolute inset-0"
      onPointerEnter={() => {
        setStageKept(true)
        setHovered(true)
      }}
      onPointerLeave={() => setHovered(false)}
      onPointerDown={() => {
        selectedAtPointerDown.current = selected
      }}
      onClick={(event) => {
        // Сдвиг на пиксель не отбрасываем: у click координата часто уезжает от нажатия,
        // а настоящий драг xyflow до этого события не доходит.
        if (event.shiftKey || event.metaKey || event.ctrlKey) return
        // Первый клик всплывает: xyflow выделяет ноду. Повторный не снимает выделение.
        if (selectedAtPointerDown.current) event.stopPropagation()
        if (!onOpen) {
          requestToggle()
          return
        }
        cancelPlayClick()
        playClickTimer.current = setTimeout(() => {
          playClickTimer.current = null
          requestToggle()
        }, PLAY_CLICK_DELAY_MS)
      }}
      onDoubleClick={(event) => {
        if (!onOpen) return
        event.stopPropagation()
        event.preventDefault()
        cancelPlayClick()
        onOpen()
      }}
    >
      {posterUrl ? (
        <img
          className={cn("absolute inset-0 z-10 h-full w-full object-cover", showStage && frameReady && "invisible")}
          src={posterUrl}
          alt=""
        />
      ) : null}
      {showStage ? <div key={mediaUrl ?? ""} ref={stageRef} className="absolute inset-0" /> : null}
      {playing ? null : (
        <span className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
          <PlayBadge className="opacity-70" />
        </span>
      )}
      {clockDurationMs > 0 ? (
        showStage ? (
          <FlowPlayClock store={timeStore} durationMs={clockDurationMs} />
        ) : (
          <span className="pointer-events-none absolute top-1.5 right-1.5 z-30 rounded-sm bg-black/60 px-1.5 py-0.5 text-[11px] text-white tabular-nums">
            {formatFlowClock(0)} - {formatFlowClock(clockDurationMs / 1000)}
          </span>
        )
      ) : null}
      {selected && !shotAwaiting ? (
        <NodeToolbar isVisible position={Position.Bottom} offset={8} className="nodrag nopan nowheel">
          <div
            className="nodrag nopan nowheel flex flex-col gap-1 overflow-hidden rounded-lg border border-ui-border bg-ui-bg-elevated p-2 shadow-md"
            style={{ width: plaqueWidth }}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
            onDoubleClick={(event) => event.stopPropagation()}
          >
            <PlaybackTransport
              playing={playing}
              onTogglePlay={togglePlay}
              onStepFrames={stepFrames}
              onGoToStart={goToStart}
              onGoToEnd={goToEnd}
            >
              <PlayerIconButton
                label={muted ? t("player.unmute") : t("player.mute")}
                onClick={() => setMuted((value) => !value)}
                icon={muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                activeState={muted}
              />
            </PlaybackTransport>
            <TimelineLanes
              doc={doc}
              timelineSources={timelineSources}
              assets={usingShotDoc && shotPlayback ? shotPlayback.audio_assets : []}
              animaticSource={animatic}
              durationMs={rulerDurationMs}
              showTrackHeaders={false}
              resetKey={playbackKey ?? mediaUrl}
              timeStore={timeStore}
              playingRef={playingRef}
              selectedIdsByTrack={EMPTY_SELECTION}
              onSeek={seek}
              onScrubStart={pauseForScrub}
              onScrubEnd={endScrub}
              onSelectClip={noopSelect}
              onMoveClip={noopMove}
              onResizeClip={noopResize}
              onDragEnd={noopDragEnd}
              scrollWithPlayhead={false}
              className="overflow-x-hidden pb-0"
            />
          </div>
        </NodeToolbar>
      ) : null}
    </div>
  )
}

function noopSelect() {}

function noopMove() {}

function noopResize() {}

function noopDragEnd() {}
